import React, { useCallback, useEffect, useRef } from 'react';

interface VideoPlayerProps {
  src: string;
  /**
   * When true, the courier may not seek past what they have already watched
   * (first viewing). When false (already completed), free seeking is allowed.
   */
  restrictSeek: boolean;
  onEnded: () => void;
  className?: string;
}

// Tolerance (seconds) so normal playback time updates are not mistaken for a
// forward seek.
const SEEK_TOLERANCE = 1;

/**
 * HTML5 video that blocks fast-forwarding. It tracks the furthest point the
 * courier has genuinely watched and snaps back any attempt to seek beyond it.
 */
const VideoPlayer: React.FC<VideoPlayerProps> = ({
  src,
  restrictSeek,
  onEnded,
  className,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const maxWatchedRef = useRef(0);

  useEffect(() => {
    maxWatchedRef.current = 0;
    const video = videoRef.current;
    if (video) {
      video.currentTime = 0;
      video.load();
    }
  }, [src]);

  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.currentTime <= maxWatchedRef.current + SEEK_TOLERANCE) {
      maxWatchedRef.current = Math.max(
        maxWatchedRef.current,
        video.currentTime,
      );
    }
  }, []);

  const handleSeeking = useCallback(() => {
    if (!restrictSeek) return;
    const video = videoRef.current;
    if (!video) return;
    if (video.currentTime > maxWatchedRef.current + SEEK_TOLERANCE) {
      video.currentTime = maxWatchedRef.current;
    }
  }, [restrictSeek]);

  return (
    <video
      ref={videoRef}
      src={src}
      controls
      controlsList="nodownload noplaybackrate"
      disablePictureInPicture
      playsInline
      onContextMenu={(e) => e.preventDefault()}
      onTimeUpdate={handleTimeUpdate}
      onSeeking={handleSeeking}
      onEnded={onEnded}
      className={className ?? 'w-full bg-black aspect-video'}
    />
  );
};

export default VideoPlayer;
